import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { DropdownChangeEvent, DropdownFilterEvent } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { GetDEPerformanceRatio } from "../../services/deliverycash/deliverycashservice";
import orderServices from "../../services/orders/orderservice";
import { userOption, userResult } from "../../models/orders/order-list-model";
import {
  DashboardHeader,
  FiltersSection,
  PerformanceCard,
  PerformanceCardSkeleton,
  EmptyState,
  tooltipContents,
} from "./PerformanceComponents";
import "./DeliveryExecutivePerformance.css";

interface PerformanceItem {
  driverID: number;
  driverName?: string;
  totalAssignedOrders: number;
  deliveredOrders: number;
  deliveryCompletionRate: number;
  monthNo?: number;
  monthName?: string;
  otherMonths?: { monthName: string; deliveryCompletionRate: number }[];
  avgDeliveryCompletionRateTillDate?: number;
  cashData?: {
    totalGrossAmount: number;
    totalHandoverAmount: number;
    cashSettlementRatePercent: number;
  };
  otherCash?: { monthName: string; cashSettlementRatePercent: number }[];
  avgSettledTillDate?: number;
}

const DeliveryExecutivePerformance: React.FC = () => {
  const toast = useRef<Toast>(null);
  const blankUserDetail = useMemo<userOption[]>(
    () => [{ userID: 0, name: "", primaryContactNbr: "" }],
    []
  );

  const [orderUsers, setOrderUsers] = useState<userOption[]>(blankUserDetail);
  const [orderUser, setOrderUser] = useState<userResult[]>([]);
  const [dropdownValue, setDropdownValue] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [performance, setPerformance] = useState<PerformanceItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);

  // Event handlers
  const handleDropdownChange = useCallback((e: DropdownChangeEvent) => {
    setDropdownValue(e.value);
  }, []);

  const handleDropdownFilter = useCallback((e: DropdownFilterEvent) => {
    if (e.filter.length > 2) {
      orderServices.getDriversList(e.filter).then((response: userOption[]) => {
        if (response.length > 0) setOrderUsers(response);
      });
    }
  }, []);

  const handleMonthChange = useCallback((e: any) => {
    setSelectedMonth(e.value as Date);
  }, []);

  // Transform orderUsers
  useEffect(() => {
    setOrderUser(
      orderUsers.map((user) => ({
        userID: user.userID,
        detail: user.userID !== 0 ? `${user.name}, ${user.primaryContactNbr}` : "",
      }))
    );
  }, [orderUsers]);

  // Fetch performance data
  useEffect(() => {
    const fetchPerformance = async () => {
      if (!dropdownValue || !selectedMonth) {
        setPerformance(null);
        setNoData(false);
        return;
      }

      setLoading(true);
      setNoData(false);

      const year = selectedMonth.getFullYear();
      const month = (selectedMonth.getMonth() + 1).toString().padStart(2, "0");
      const monthNumber = parseInt(month, 10);
      const payload = { inputMonth: `${year}-${month}`, driverID: dropdownValue };

      try {
        const response = await GetDEPerformanceRatio(payload);

        if (
          response?.deliveryPerformanceList?.length > 0 ||
          response?.cashSettlementRateList?.length > 0
        ) {
          const matched = response.deliveryPerformanceList?.find(
            (d: any) => Number(d.monthNo) === monthNumber
          );
          const others = response.deliveryPerformanceList?.filter(
            (d: any) => Number(d.monthNo) !== monthNumber
          );
          const matchedCash = response.cashSettlementRateList?.find(
            (c: any) => Number(c.monthNo) === monthNumber
          );
          const othersCash = response.cashSettlementRateList?.filter(
            (c: any) => Number(c.monthNo) !== monthNumber
          );

          if (matched) {
            const mapped: PerformanceItem = {
              driverID: matched.driverID,
              driverName: matched.driverName,
              totalAssignedOrders: matched.totalAssignedOrders ?? 0,
              deliveredOrders: matched.deliveredOrders ?? 0,
              deliveryCompletionRate: matched.deliveryCompletionRate ?? 0,
              monthNo: matched.monthNo,
              monthName: matched.monthName,
              otherMonths: others?.map((o: any) => ({
                monthName: o.monthName,
                deliveryCompletionRate: o.deliveryCompletionRate,
              })),
              avgDeliveryCompletionRateTillDate:
                response.avgDeliveryCompletionRateTillDate ?? 0,
              cashData: matchedCash
                ? {
                    totalGrossAmount: matchedCash.totalGrossAmount ?? 0,
                    totalHandoverAmount: matchedCash.totalHandoverAmount ?? 0,
                    cashSettlementRatePercent: matchedCash.cashSettlementRatePercent ?? 0,
                  }
                : undefined,
              otherCash: othersCash?.map((c: any) => ({
                monthName: c.monthName,
                cashSettlementRatePercent: c.cashSettlementRatePercent ?? 0,
              })),
              avgSettledTillDate: response.avgSettledTillDate ?? 0,
            };

            setPerformance(mapped);
            setNoData(false);
          } else {
            setPerformance(null);
            setNoData(true);
          }
        } else {
          setPerformance(null);
          setNoData(true);
        }
      } catch (err) {
        console.error("Error fetching performance:", err);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch performance data",
          life: 3000,
        });
        setPerformance(null);
        setNoData(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPerformance();
  }, [dropdownValue, selectedMonth]);

  const formattedMonth = useMemo(
    () => selectedMonth.toLocaleString("en-US", { month: "short", year: "numeric" }),
    [selectedMonth]
  );

  // Render cards content
  const renderCards = () => {
    if (loading) {
      return (
        <>
          <PerformanceCardSkeleton />
          <PerformanceCardSkeleton />
          <PerformanceCardSkeleton />
        </>
      );
    }

    if (noData) {
      return (
        <EmptyState
          icon="pi-inbox"
          gradientClass="gradient-orange"
          title="No Data Available"
          description="No performance data found for the selected driver and month."
        />
      );
    }

    if (!performance) {
      return (
        <EmptyState
          icon="pi-user-plus"
          gradientClass="gradient-purple"
          title="Select Driver to Continue"
          description={`Choose a driver above to view detailed performance metrics for <strong class="empty-state-highlight">${formattedMonth}</strong>`}
        />
      );
    }

    return (
      <>
        {/* Delivery Card */}
        <PerformanceCard
          title="Delivery Completion Rate"
          value={`${performance.deliveryCompletionRate}%`}
          gradientClass="gradient-green"
          icon="pi-box"
          tooltipId="completion-info"
          index={0}
          tooltipContent={tooltipContents.delivery}
        >
          <div className="stat-grid">
            <div className="stat-item">
              <p className="stat-label">Assigned</p>
              <p className="stat-value">{performance.totalAssignedOrders}</p>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <p className="stat-label">Delivered</p>
              <p className="stat-value success">{performance.deliveredOrders}</p>
            </div>
          </div>

          <div className="info-badge badge-green">
            <i className="pi pi-chart-line mr-2"></i>
            <span>
              YTD Average:{" "}
              <strong>{performance.avgDeliveryCompletionRateTillDate?.toFixed(1)}%</strong>
            </span>
          </div>

          {performance.otherMonths && performance.otherMonths.length > 0 && (
            <div className="other-months">
              {performance.otherMonths
                .map((m) => `${m.monthName}: ${m.deliveryCompletionRate?.toFixed(0)}%`)
                .join(" • ")}
            </div>
          )}
        </PerformanceCard>

        {/* Cash Card */}
        <PerformanceCard
          title="Cash Settlement Ratio"
          value={`${performance.cashData?.cashSettlementRatePercent ?? 0}%`}
          gradientClass="gradient-teal"
          icon="pi-wallet"
          tooltipId="cash-info"
          index={1}
          tooltipContent={tooltipContents.cash}
        >
          <div className="stat-grid">
            <div className="stat-item">
              <p className="stat-label">Gross Amount</p>
              <p className="stat-value stat-value-sm">
                ₹{performance.cashData?.totalGrossAmount?.toLocaleString("en-IN")}
              </p>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <p className="stat-label">Handover</p>
              <p className="stat-value success stat-value-sm">
                ₹{performance.cashData?.totalHandoverAmount?.toLocaleString("en-IN")}
              </p>
            </div>
          </div>

          <div className="info-badge badge-teal">
            <i className="pi pi-chart-line mr-2"></i>
            <span>
              YTD Average: <strong>{performance.avgSettledTillDate?.toFixed(1)}%</strong>
            </span>
          </div>

          {performance.otherCash && performance.otherCash.length > 0 && (
            <div className="other-months">
              {performance.otherCash
                .map((m) => `${m.monthName}: ${m.cashSettlementRatePercent?.toFixed(0)}%`)
                .join(" • ")}
            </div>
          )}
        </PerformanceCard>

        {/* Time Card */}
        <PerformanceCard
          title="Delivery Time Ratio"
          value="85%"
          gradientClass="gradient-pink"
          icon="pi-clock"
          tooltipId="time-info"
          index={2}
          tooltipContent={tooltipContents.time}
        >
          <div className="info-badge badge-pink info-badge-full">
            <i className="pi pi-check-circle text-green-600 text-xl mr-2"></i>
            <span>Consistently on-time this month 🎯</span>
          </div>

          <div className="trend-box">
            <div className="trend-header">
              <span className="trend-label">Performance Trend</span>
              <span className="trend-value">▲ 5%</span>
            </div>
            <p className="trend-text">Improved from 80% last month. Great work! 👏</p>
          </div>
        </PerformanceCard>
      </>
    );
  };

  return (
    <div className="dashboard-container">
      <Toast ref={toast} />
      <div className="dashboard-content">
        <DashboardHeader />
        <FiltersSection
          orderUser={orderUser}
          dropdownValue={dropdownValue}
          selectedMonth={selectedMonth}
          onDropdownChange={handleDropdownChange}
          onDropdownFilter={handleDropdownFilter}
          onMonthChange={handleMonthChange}
        />
        <div className="cards-container">{renderCards()}</div>
      </div>
    </div>
  );
};

export default DeliveryExecutivePerformance;
